import {defineStore} from 'pinia';
import {getCookie, setCookie, scrollTop} from "@js/helpers.ts";
import {checkDocument} from "@js/helpers.ts";

export const useCartStore = defineStore('cartStore', {
    state: () => ({
        items: [],
        loadings: {
            cart_loading: true,
        },
        cart_products: [],
        form: {
            full_name: '',
            phone: '',
            email: '',
            comment: '',
            captcha_token: '',

            delivery_type: 'pickup', // 'pickup' | 'inside_mkad' | 'outside_mkad' | 'russia'
            delivery_distance: 50, // расстояние от МКАД в км (если outside_mkad)
            delivery_address: '', // адрес доставки
            need_lift: true, // подъем на этаж

            payment_type: 'cash', // 'cash' | 'card' | 'invoice'
        },
        form_errors: [],
        isCartClearModalVisible: false,
        cart_form_successfully_sent: false,
    }),
    getters: {
        totalQuantity: (state) => state.items.reduce((sum, i) => sum + i.qty, 0),
        totalPrice: (state) => state.items.reduce((sum, i) => sum + i.qty * i.price, 0),
        findItem: (state) => (id) => state.items.find(i => i.id === id),
        deliveryCost: (state) => {
            const price = state.items.reduce((sum, i) => sum + i.qty * i.price, 0);
            switch (state.form.delivery_type) {
                case 'pickup':
                    return 0;
                case 'inside_mkad':
                    return price >= 50000 ? 0 : 2500;
                case 'outside_mkad':
                    return (state.form.delivery_distance || 0) * 70;
                case 'russia':
                    return 0; // Менеджер уточнит
                default:
                    return 0;
            }
        },
    },
    actions: {
        /**
         * Рассчитывает площадь в м² по длине и ширине в см
         */
        calculateArea(lengthCm, widthCm) {
            const lengthM = parseFloat(lengthCm) / 100;
            const widthM = parseFloat(widthCm) / 100;
            return parseFloat((lengthM * widthM).toFixed(4));
        },
        getProduct(productId) {
            return this.items.find(i => i.id === productId) ?? null;
        },
        getProductTotalPrice(productId) {
            const product = this.getProduct(productId)
            if (product.unit === 'm2') {
                return Math.ceil(product.qty * product.price);
            } else {
                return Math.ceil((this.calculateArea(product.length, product.width) * product.qty * product.price));
            }
        },
        allProductsPriceSum() {
            return this.items.reduce(
                (sum, product) => sum + this.getProductTotalPrice(product.id),
                0
            )
        },
        loadCart() {
            const raw = getCookie('cart') || '[]';
            try {
                this.items = JSON.parse(raw);
            } catch {
                this.items = [];
            }
        },
        persist() {
            setCookie('cart', JSON.stringify(this.items), 525600); //На год
        },
        addToCart(product, unit, qty) {
            const existing = this.findItem(product.id);
            if (existing) {
                existing.qty = parseFloat((existing.qty + qty).toFixed(3));
            } else {
                this.items.push({
                    id: product.id,
                    unit,
                    qty,
                    length: product.length,
                    width: product.width,
                    price: product.price,
                });
            }
            this.persist();
        },
        updateQty(id, unit, qty) {
            const item = this.findItem(id);
            if (item) {
                item.unit = unit;
                item.qty = qty;
                if (item.qty <= 0) {
                    this.remove(id);
                    return;
                }
                this.persist();
            }
        },
        remove(id) {
            this.items = this.items.filter(i => !(i.id === id));
            this.persist();
            this.getCartData()
        },
        clearCart() {
            this.items = [];
            this.persist();
            this.isCartClearModalVisible = false
        },
        hasItem(id) {
            // Проверяет наличие товара с данным id через куки
            const raw = getCookie('cart') || '[]';
            try {
                const items = JSON.parse(raw);
                return items.some(i => i.id === id) || this.items.some(i => i.id === id);
            } catch {
                return false;
            }
        },
        clear() {
            this.items = [];
            this.persist();
        },
        getCartData() {
            this.loadings.cart_loading = true
            axios.post('/axios/get-products-by-ids', {
                ids: this.items.map(i => i.id) ?? [],
            }).then(response => {
                this.cart_products = response.data.products ?? [];
                this.loadings.cart_loading = false;
            }).catch(error => {
                this.loadings.cart_loading = false;
            });
        },
        sendForm() {
            if (checkDocument()) return;

            this.form_errors = []
            this.loadings.sending = false
            this.cart_form_successfully_sent = false
            this.form.captcha_token = ''

            const captchaId = 'smart-captcha-container-cart-form'

            // 1. Удаление старого контейнера + destroy капчи
            const existing = document.getElementById(captchaId)
            if (existing) {
                try {
                    window.smartCaptcha.destroy(existing.dataset.widgetId)
                } catch (e) {
                }
                existing.remove()
            }

            // 2. Создание нового скрытого контейнера
            const container = document.createElement('div')
            container.id = captchaId
            container.style.position = 'absolute'
            container.style.zIndex = '-1'
            container.style.left = '-9999px'
            container.style.top = '-9999px'
            document.body.appendChild(container)

            let tokenReceived = false

            // 3. Рендер капчи
            const widgetId = window.smartCaptcha.render(captchaId, {
                sitekey: import.meta.env.VITE_YANDEX_SMARTCAPTCHA_SECRET,
                invisible: true,
                hideShield: true,
                callback: (token) => {
                    tokenReceived = true
                    this.loadings.sending = true
                    this.form.captcha_token = token

                    axios.post('/axios/form/send-cart', {
                        bitrix_title: 'Оформление заказа на сайте',
                        ...this.form,
                        cart_products: this.items,
                    }).then(response => {
                        scrollTop()
                        this.loadings.sending = false
                        this.cart_form_successfully_sent = true
                        this.clearCart()
                        ym(102081669, 'reachGoal', 'order_form_submit')
                        try {
                            window.smartCaptcha.destroy(widgetId)
                        } catch (e) {
                        }
                    }).catch(error => {
                        this.loadings.sending = false
                        if (error.response?.status === 422) {
                            this.form_errors = error.response.data.errors
                        }
                    })
                }
            })

            // 4. Привязка widgetId к контейнеру
            document.getElementById(captchaId).dataset.widgetId = widgetId

            // 5. Отслеживание ручного закрытия
            window.smartCaptcha.subscribe(widgetId, 'challenge-hidden', () => {
                if (!tokenReceived) {
                    this.loadings.sending = false
                    this.form_errors = {
                        captcha_token: ['Вы закрыли капчу. Повторите попытку.']
                    }
                    try {
                        window.smartCaptcha.reset(widgetId)
                    } catch (e) {
                    }
                }
            })

            // 6. Запуск капчи
            setTimeout(() => {
                try {
                    window.smartCaptcha.execute(widgetId)
                } catch (e) {
                    this.loadings.sending = false
                    this.form_errors = {
                        captcha_token: ['Не удалось запустить капчу. Попробуйте снова.']
                    }
                }
            }, 100)
        },
    }
});
